import { zodResolver } from "@hookform/resolvers/zod";
import {
	type ColumnDef,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import {
	ArrowLeft,
	ArrowUpDown,
	ChevronLeft,
	ChevronRight,
	Edit,
	Key,
	Mail,
	Plus,
	RefreshCw,
	Search,
	Trash2,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import FieldError from "../components/FieldError";
import LoadingSpinner from "../components/LoadingSpinner";
import SyncConfirmModal from "../components/SyncConfirmModal";
import SyncResultsModal from "../components/SyncResultsModal";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../components/ui/table";
import ValidationBanner from "../components/ValidationBanner";
import { useAuth } from "../context/AuthContext";
import { useWallets } from "../context/WalletContext";
import useConfirmDialog from "../hooks/useConfirmDialog";
import { useUsersQuery } from "../hooks/useUsersQuery";
import { cn } from "../lib/utils";
import {
	type CreateUserFormData,
	createUserSchema,
	type EditUserFormData,
	editUserSchema,
	type ResetPasswordFormData,
	resetPasswordSchema,
} from "../schemas/user";
import {
	getSyncStatus,
	type SyncMeta,
	type SyncResult,
	triggerCognitoSync,
} from "../services/api/syncApi";
import { authService } from "../services/auth/authService";
import type { User } from "../types";

const UserManagement: React.FC = () => {
	const { isAdmin } = useAuth();
	const {
		data: users = [],
		isLoading: usersLoading,
		refetch: refetchUsers,
	} = useUsersQuery(!!isAdmin);
	const [showForm, setShowForm] = useState(false);
	const [editingUser, setEditingUser] = useState<User | null>(null);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
	const [resetPasswordPermanent, setResetPasswordPermanent] = useState(true);
	const [resetPasswordError, setResetPasswordError] = useState("");
	const [resetPasswordSuccess, setResetPasswordSuccess] = useState("");
	const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
	const userFormBannerRef = useRef<HTMLDivElement>(null);
	const resetFormBannerRef = useRef<HTMLDivElement>(null);

	const userSchema = useMemo(
		() => (editingUser ? editUserSchema : createUserSchema),
		[editingUser],
	);

	const userForm = useForm<CreateUserFormData | EditUserFormData>({
		resolver: zodResolver(userSchema),
		defaultValues: {
			name: "",
			email: "",
			password: "",
			role: "partner",
			walletIds: [],
		},
		mode: "onSubmit",
	});

	const resetPasswordForm = useForm<ResetPasswordFormData>({
		resolver: zodResolver(resetPasswordSchema),
		defaultValues: {
			newPassword: "",
			confirmPassword: "",
		},
		mode: "onSubmit",
	});
	const [syncLoading, setSyncLoading] = useState(false);
	const [showSyncConfirm, setShowSyncConfirm] = useState(false);
	const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
	const [showSyncResults, setShowSyncResults] = useState(false);
	const [lastSyncMeta, setLastSyncMeta] = useState<SyncMeta | null>(null);

	const { wallets } = useWallets();
	const { refreshUser: _refreshUser } = useAuth();
	const { confirm } = useConfirmDialog();

	const [sorting, setSorting] = useState<SortingState>([]);
	const [globalFilter, setGlobalFilter] = useState("");

	const loadUsers = useCallback(() => {
		refetchUsers();
	}, [refetchUsers]);

	useEffect(() => {
		getSyncStatus()
			.then((meta) => setLastSyncMeta(meta ?? null))
			.catch(() => setLastSyncMeta(null));
	}, []);

	const handleSyncConfirmed = async () => {
		setSyncLoading(true);
		setShowSyncConfirm(false);
		setError("");
		try {
			const result = await triggerCognitoSync();
			setSyncResult(result);
			setShowSyncResults(true);
			const meta = await getSyncStatus();
			setLastSyncMeta(meta ?? null);
			await loadUsers();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Sync failed");
		} finally {
			setSyncLoading(false);
		}
	};

	const onValidUserSubmit = async (
		data: CreateUserFormData | EditUserFormData,
	) => {
		setError("");
		setSuccessMessage("");

		try {
			if (editingUser) {
				await authService.updateUser(editingUser.id, {
					name: data.name,
					role: data.role,
					wallet_ids: data.walletIds,
				});

				// User-wallet assignment is managed server-side via user.wallet_ids

				setSuccessMessage("User updated successfully!");
			} else {
				const createData = data as CreateUserFormData;
				const newUser = await authService.createUser(
					createData.email,
					createData.password,
					createData.name,
					createData.role,
				);

				if (createData.walletIds.length > 0) {
					await authService.updateUser(newUser.id, {
						wallet_ids: createData.walletIds,
					});
				}

				setSuccessMessage(
					`User created successfully! Login email: ${createData.email}. Share the temporary password with the user securely.`,
				);
			}

			await loadUsers();
			resetForm();
			setTimeout(() => setSuccessMessage(""), 5000);
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Failed to save user");
		}
	};

	const onInvalidUserSubmit = () => {
		userFormBannerRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const handleDelete = async (userId: string) => {
		const ok = await confirm({
			title: "Delete User",
			message: "Are you sure you want to delete this user?",
			confirmLabel: "Delete",
			variant: "danger",
		});
		if (!ok) return;

		try {
			await authService.deleteUser(userId);
			await loadUsers();
			setSuccessMessage("User deleted successfully");
			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (_err) {
			setError("Failed to delete user");
		}
	};

	const handleEdit = (user: User) => {
		setEditingUser(user);
		userForm.reset({
			name: user.name,
			email: user.email,
			password: "",
			role: user.role,
			walletIds: user.wallet_ids || [],
		});
		setShowForm(true);
	};

	const resetForm = () => {
		userForm.reset({
			name: "",
			email: "",
			password: "",
			role: "partner",
			walletIds: [],
		});
		setEditingUser(null);
		setShowForm(false);
		setError("");
	};

	const toggleWalletAssignment = (walletId: string) => {
		const current = userForm.getValues("walletIds") ?? [];
		const next = current.includes(walletId)
			? current.filter((id: string) => id !== walletId)
			: [...current, walletId];
		userForm.setValue("walletIds", next);
	};

	const openResetPasswordModal = (user: User) => {
		setResetPasswordUser(user);
		resetPasswordForm.reset({ newPassword: "", confirmPassword: "" });
		setResetPasswordPermanent(true);
		setResetPasswordError("");
		setResetPasswordSuccess("");
	};

	const closeResetPasswordModal = () => {
		setResetPasswordUser(null);
		resetPasswordForm.reset({ newPassword: "", confirmPassword: "" });
		setResetPasswordError("");
		setResetPasswordSuccess("");
	};

	const onValidResetPassword = async (data: ResetPasswordFormData) => {
		if (!resetPasswordUser) return;
		setResetPasswordError("");
		setResetPasswordSuccess("");
		setResetPasswordLoading(true);
		try {
			await authService.resetUserPassword?.(
				resetPasswordUser.id,
				data.newPassword,
				resetPasswordPermanent,
			);
			setResetPasswordSuccess(
				"Password has been set successfully. The user can now log in with the new password.",
			);
		} catch (err) {
			setResetPasswordError(
				err instanceof Error ? err.message : "Failed to reset password",
			);
		} finally {
			setResetPasswordLoading(false);
		}
	};

	const onInvalidResetPassword = () => {
		resetFormBannerRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const columns = useMemo<ColumnDef<User>[]>(
		() => [
			{
				accessorKey: "name",
				header: ({ column }) => (
					<Button
						variant="ghost"
						size="sm"
						className="-ml-3 h-8"
						onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
					>
						Name
						<ArrowUpDown size={14} />
					</Button>
				),
			},
			{
				accessorKey: "email",
				header: ({ column }) => (
					<Button
						variant="ghost"
						size="sm"
						className="-ml-3 h-8"
						onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
					>
						Email
						<ArrowUpDown size={14} />
					</Button>
				),
			},
			{
				accessorKey: "role",
				header: "Role",
				cell: ({ row }) => (
					<Badge
						variant={
							row.original.role === "admin"
								? "destructive"
								: row.original.role === "stellar_admin"
									? "outline"
									: "secondary"
						}
					>
						{row.original.role === "stellar_admin"
							? "Stellar Admin"
							: row.original.role}
					</Badge>
				),
				filterFn: "equals",
			},
			{
				id: "wallets",
				header: "Assigned Wallets",
				accessorFn: (row) =>
					(row.wallet_ids ?? [])
						.map((wId) => wallets.find((w) => w.id === wId)?.wallet_name)
						.filter(Boolean)
						.join(", "),
				cell: ({ row }) => {
					const wIds = row.original.wallet_ids ?? [];
					if (wIds.length === 0) {
						return (
							<span className="text-(--color-muted-foreground) text-sm">
								None
							</span>
						);
					}
					return (
						<div className="flex flex-wrap gap-1">
							{wIds.map((wId) => {
								const wallet = wallets.find((w) => w.id === wId);
								return wallet ? (
									<Badge key={wId} variant="outline" className="text-xs">
										{wallet.wallet_name}
									</Badge>
								) : null;
							})}
						</div>
					);
				},
			},
			{
				accessorKey: "last_login",
				header: ({ column }) => (
					<Button
						variant="ghost"
						size="sm"
						className="-ml-3 h-8"
						onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
					>
						Last Login
						<ArrowUpDown size={14} />
					</Button>
				),
				cell: ({ row }) => (
					<span className="text-(--color-muted-foreground) text-sm">
						{row.original.last_login
							? new Date(row.original.last_login).toLocaleDateString()
							: "Never"}
					</span>
				),
			},
			{
				id: "actions",
				header: () => <span className="block text-center">Actions</span>,
				cell: ({ row }) => (
					<div className="flex items-center justify-center gap-2">
						<Button
							type="button"
							variant="outline"
							size="icon"
							onClick={() => handleEdit(row.original)}
							title="Edit user"
						>
							<Edit size={14} />
						</Button>
						<Button
							type="button"
							variant="outline"
							size="icon"
							onClick={() => openResetPasswordModal(row.original)}
							title="Reset password"
						>
							<Key size={14} />
						</Button>
						<Button
							type="button"
							variant="destructive"
							size="icon"
							onClick={() => handleDelete(row.original.id)}
							title="Delete user"
						>
							<Trash2 size={14} />
						</Button>
					</div>
				),
				enableSorting: false,
			},
		],
		[wallets],
	);

	const table = useReactTable({
		data: users,
		columns,
		state: { sorting, globalFilter },
		onSortingChange: setSorting,
		onGlobalFilterChange: setGlobalFilter,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: { pagination: { pageSize: 10 } },
	});

	// Show form view
	if (showForm) {
		return (
			<div className="min-h-[calc(100vh-56px)] bg-(--color-background) px-4 pt-20 pb-8 md:px-6">
				<div className="mx-auto max-w-[800px]">
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={resetForm}
						className="mb-6"
					>
						<ArrowLeft size={16} />
						Back to Users
					</Button>

					<Card className="p-6">
						<h2 className="mb-6 font-semibold text-(--color-foreground) text-xl">
							{editingUser ? "Edit User" : "Add New User"}
						</h2>

						<form
							onSubmit={userForm.handleSubmit(
								onValidUserSubmit,
								onInvalidUserSubmit,
							)}
						>
							<div ref={userFormBannerRef}>
								{error && (
									<Alert variant="destructive" className="mb-6">
										<AlertDescription>{error}</AlertDescription>
									</Alert>
								)}

								{successMessage && (
									<Alert variant="success" className="mb-6">
										<AlertDescription>{successMessage}</AlertDescription>
									</Alert>
								)}

								{Object.keys(userForm.formState.errors).length > 0 && (
									<ValidationBanner
										message="Please fix the errors below."
										fields={Object.values(userForm.formState.errors)
											.filter(
												(e): e is { message?: string } =>
													!!e && typeof e === "object",
											)
											.map((e) => e.message)
											.filter((m): m is string => !!m)}
										aria-live="polite"
									/>
								)}
							</div>

							{/* Name */}
							<div className="mb-4 space-y-1.5">
								<Label htmlFor="user-name">
									Name <span className="text-(--color-error-500)">*</span>
								</Label>
								<Input
									id="user-name"
									type="text"
									{...userForm.register("name")}
									className={
										userForm.formState.errors.name
											? "border-(--color-error-500)"
											: undefined
									}
									aria-invalid={!!userForm.formState.errors.name}
								/>
								{userForm.formState.errors.name?.message && (
									<FieldError
										message={String(userForm.formState.errors.name.message)}
									/>
								)}
							</div>

							{/* Email */}
							<div className="mb-4 space-y-1.5">
								<Label htmlFor="user-email">
									Email <span className="text-(--color-error-500)">*</span>
								</Label>
								<Input
									id="user-email"
									type="email"
									{...userForm.register("email")}
									disabled={!!editingUser}
									className={
										userForm.formState.errors.email
											? "border-(--color-error-500)"
											: undefined
									}
									aria-invalid={!!userForm.formState.errors.email}
								/>
								{userForm.formState.errors.email?.message && (
									<FieldError
										message={String(userForm.formState.errors.email.message)}
									/>
								)}
								{editingUser && (
									<p className="text-(--color-muted-foreground) text-xs">
										Email cannot be changed
									</p>
								)}
							</div>

							{/* Password (create only) */}
							{!editingUser && (
								<div className="mb-4 space-y-1.5">
									<Label htmlFor="user-password">
										Password <span className="text-(--color-error-500)">*</span>
									</Label>
									<Input
										id="user-password"
										type="text"
										{...userForm.register("password")}
										placeholder="Enter temporary password"
										className={
											userForm.formState.errors.password
												? "border-(--color-error-500)"
												: undefined
										}
										aria-invalid={!!userForm.formState.errors.password}
									/>
									{userForm.formState.errors.password?.message && (
										<FieldError
											message={String(
												userForm.formState.errors.password.message,
											)}
										/>
									)}
									<p className="text-(--color-muted-foreground) text-xs">
										User will use this password to login. Share it securely.
									</p>
								</div>
							)}

							{/* Role */}
							<div className="mb-4 space-y-2">
								{/* biome-ignore lint/a11y/noLabelWithoutControl: Radio group label */}
								<Label>
									Role <span className="text-(--color-error-500)">*</span>
								</Label>
								<div className="flex gap-4">
									<label
										className={cn(
											"flex flex-1 cursor-pointer items-center gap-3 rounded-(--radius-sm) border p-4 transition-colors",
											userForm.watch("role") === "partner"
												? "border-(--color-primary-500) bg-(--color-accent-muted)"
												: "border-(--color-border) bg-(--color-card)",
										)}
									>
										<input
											type="radio"
											{...userForm.register("role")}
											value="partner"
										/>
										<div>
											<div className="font-semibold text-(--color-foreground) text-sm">
												Partner
											</div>
											<div className="text-(--color-muted-foreground) text-xs">
												Limited Access
											</div>
										</div>
									</label>
									<label
										className={cn(
											"flex flex-1 cursor-pointer items-center gap-3 rounded-(--radius-sm) border p-4 transition-colors",
											userForm.watch("role") === "admin"
												? "border-(--color-primary-500) bg-(--color-accent-muted)"
												: "border-(--color-border) bg-(--color-card)",
										)}
									>
										<input
											type="radio"
											{...userForm.register("role")}
											value="admin"
										/>
										<div>
											<div className="font-semibold text-(--color-foreground) text-sm">
												Admin
											</div>
											<div className="text-(--color-muted-foreground) text-xs">
												Full Access
											</div>
										</div>
									</label>
									<label
										className={cn(
											"flex flex-1 cursor-pointer items-center gap-3 rounded-(--radius-sm) border p-4 transition-colors",
											userForm.watch("role") === "stellar_admin"
												? "border-(--color-primary-500) bg-(--color-accent-muted)"
												: "border-(--color-border) bg-(--color-card)",
										)}
									>
										<input
											type="radio"
											{...userForm.register("role")}
											value="stellar_admin"
										/>
										<div>
											<div className="font-semibold text-(--color-foreground) text-sm">
												Stellar Admin
											</div>
											<div className="text-(--color-muted-foreground) text-xs">
												Stellar Access
											</div>
										</div>
									</label>
								</div>
							</div>

							{/* Wallet assignment */}
							{userForm.watch("role") === "partner" && (
								<div className="mb-4 space-y-2">
									{/* biome-ignore lint/a11y/noLabelWithoutControl: Checkbox group label */}
									<Label>Assign Wallets</Label>
									<div className="max-h-[300px] overflow-auto rounded-(--radius-sm) border border-(--color-border) bg-(--color-muted) p-4">
										{wallets.length === 0 ? (
											<p className="py-5 text-center text-(--color-muted-foreground) text-sm">
												No wallets available. Create a wallet first.
											</p>
										) : (
											wallets.map((wallet) => (
												<label
													key={wallet.id}
													className="mb-2.5 flex cursor-pointer items-center gap-3 rounded-(--radius-sm) border border-(--color-border) bg-(--color-card) p-3 transition-colors last:mb-0 hover:border-(--color-primary)"
												>
													<input
														type="checkbox"
														checked={(
															userForm.watch("walletIds") ?? []
														).includes(wallet.id)}
														onChange={() => toggleWalletAssignment(wallet.id)}
													/>
													<div>
														<div className="font-semibold text-(--color-foreground) text-sm">
															{wallet.wallet_name}
														</div>
														<div className="text-(--color-muted-foreground) text-xs">
															{wallet.company_name}
														</div>
													</div>
												</label>
											))
										)}
									</div>
								</div>
							)}

							<div className="mt-8 flex justify-end gap-3">
								<Button type="button" variant="outline" onClick={resetForm}>
									Cancel
								</Button>
								<Button type="submit">
									{editingUser ? "Update User" : "Create User"}
								</Button>
							</div>
						</form>
					</Card>
				</div>
			</div>
		);
	}

	if (usersLoading) {
		return <LoadingSpinner size="lg" message="Loading users..." centered />;
	}

	return (
		<div className="min-h-[calc(100vh-56px)] bg-(--color-background) px-4 pt-20 pb-8 md:px-6">
			<div className="mx-auto max-w-[1200px]">
				{error && (
					<Alert variant="destructive" className="mb-6">
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}
				{successMessage && (
					<Alert variant="success" className="mb-6">
						<Mail size={16} />
						<AlertDescription>{successMessage}</AlertDescription>
					</Alert>
				)}

				{/* Page header */}
				<div
					className={cn(
						"flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between",
						lastSyncMeta ? "mb-2" : "mb-8",
					)}
				>
					<div>
						<h1 className="font-semibold text-(--color-foreground) text-3xl">
							User Management
						</h1>
						<p className="mt-1 text-(--color-muted-foreground) text-sm">
							Manage partner and admin users
						</p>
					</div>
					<div className="flex shrink-0 gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								setError("");
								setShowSyncConfirm(true);
							}}
							disabled={syncLoading}
						>
							<RefreshCw size={16} />
							{syncLoading ? "Syncing…" : "Sync Users"}
						</Button>
						<Button type="button" onClick={() => setShowForm(true)}>
							<Plus size={16} />
							Add User
						</Button>
					</div>
				</div>

				{lastSyncMeta && (
					<p className="mb-6 text-(--color-muted-foreground) text-sm">
						Last synced: {new Date(lastSyncMeta.last_sync_at).toLocaleString()}
					</p>
				)}

				{/* Search */}
				<div className="relative mb-4">
					<Search
						size={16}
						className="absolute top-1/2 left-3 -translate-y-1/2 text-(--color-muted-foreground)"
					/>
					<Input
						placeholder="Search users by name, email, or role…"
						value={globalFilter}
						onChange={(e) => setGlobalFilter(e.target.value)}
						className="pl-9"
					/>
				</div>

				{/* Users table */}
				<Card>
					<Table>
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<TableHead key={header.id}>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef.header,
														header.getContext(),
													)}
										</TableHead>
									))}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
							{table.getRowModel().rows.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={columns.length}
										className="py-8 text-center text-(--color-muted-foreground)"
									>
										{globalFilter
											? "No users match your search."
											: "No users found."}
									</TableCell>
								</TableRow>
							) : (
								table.getRowModel().rows.map((row) => (
									<TableRow key={row.id}>
										{row.getVisibleCells().map((cell) => (
											<TableCell key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</TableCell>
										))}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</Card>

				{/* Pagination */}
				{table.getPageCount() > 1 && (
					<div className="mt-4 flex items-center justify-between">
						<p className="text-(--color-muted-foreground) text-sm">
							Showing{" "}
							{table.getState().pagination.pageIndex *
								table.getState().pagination.pageSize +
								1}
							–
							{Math.min(
								(table.getState().pagination.pageIndex + 1) *
									table.getState().pagination.pageSize,
								table.getFilteredRowModel().rows.length,
							)}{" "}
							of {table.getFilteredRowModel().rows.length} users
						</p>
						<div className="flex items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => table.previousPage()}
								disabled={!table.getCanPreviousPage()}
							>
								<ChevronLeft size={14} />
								Previous
							</Button>
							<span className="text-(--color-muted-foreground) text-sm">
								Page {table.getState().pagination.pageIndex + 1} of{" "}
								{table.getPageCount()}
							</span>
							<Button
								variant="outline"
								size="sm"
								onClick={() => table.nextPage()}
								disabled={!table.getCanNextPage()}
							>
								Next
								<ChevronRight size={14} />
							</Button>
						</div>
					</div>
				)}

				{/* Reset password dialog */}
				<Dialog
					open={!!resetPasswordUser}
					onOpenChange={(open) => {
						if (!open) closeResetPasswordModal();
					}}
				>
					<DialogContent className="sm:max-w-[420px]">
						<DialogHeader>
							<DialogTitle>Reset password</DialogTitle>
							<DialogDescription>
								Set a new password for {resetPasswordUser?.email}. Share it with
								the user securely.
							</DialogDescription>
						</DialogHeader>

						<form
							onSubmit={resetPasswordForm.handleSubmit(
								onValidResetPassword,
								onInvalidResetPassword,
							)}
						>
							{resetPasswordError && (
								<Alert variant="destructive" className="mb-4">
									<AlertDescription>{resetPasswordError}</AlertDescription>
								</Alert>
							)}
							{resetPasswordSuccess && (
								<Alert variant="success" className="mb-4">
									<AlertDescription>{resetPasswordSuccess}</AlertDescription>
								</Alert>
							)}

							{!resetPasswordSuccess && (
								<>
									<div ref={resetFormBannerRef}>
										{Object.keys(resetPasswordForm.formState.errors).length >
											0 && (
											<ValidationBanner
												message="Please fix the errors below."
												fields={Object.values(
													resetPasswordForm.formState.errors,
												)
													.filter(
														(e): e is { message?: string } =>
															!!e && typeof e === "object",
													)
													.map((e) => e.message)
													.filter((m): m is string => !!m)}
												aria-live="polite"
											/>
										)}
									</div>
									<div className="mb-4 space-y-1.5">
										<Label htmlFor="reset-pwd-password">
											New password{" "}
											<span className="text-(--color-error-500)">*</span>
										</Label>
										<Input
											id="reset-pwd-password"
											type="text"
											{...resetPasswordForm.register("newPassword")}
											placeholder="At least 8 characters"
											autoComplete="new-password"
											className={
												resetPasswordForm.formState.errors.newPassword
													? "border-(--color-error-500)"
													: undefined
											}
											aria-invalid={
												!!resetPasswordForm.formState.errors.newPassword
											}
										/>
										{resetPasswordForm.formState.errors.newPassword
											?.message && (
											<FieldError
												message={String(
													resetPasswordForm.formState.errors.newPassword
														.message,
												)}
											/>
										)}
									</div>
									<div className="mb-4 space-y-1.5">
										<Label htmlFor="reset-pwd-confirm">
											Confirm password{" "}
											<span className="text-(--color-error-500)">*</span>
										</Label>
										<Input
											id="reset-pwd-confirm"
											type="text"
											{...resetPasswordForm.register("confirmPassword")}
											placeholder="Re-enter password"
											autoComplete="new-password"
											className={
												resetPasswordForm.formState.errors.confirmPassword
													? "border-(--color-error-500)"
													: undefined
											}
											aria-invalid={
												!!resetPasswordForm.formState.errors.confirmPassword
											}
										/>
										{resetPasswordForm.formState.errors.confirmPassword
											?.message && (
											<FieldError
												message={String(
													resetPasswordForm.formState.errors.confirmPassword
														.message,
												)}
											/>
										)}
									</div>
									<div className="mb-6 flex items-center gap-2">
										<input
											id="reset-pwd-permanent"
											type="checkbox"
											checked={resetPasswordPermanent}
											onChange={(e) =>
												setResetPasswordPermanent(e.target.checked)
											}
										/>
										<label
											htmlFor="reset-pwd-permanent"
											className="cursor-pointer text-(--color-foreground) text-sm"
										>
											User can log in immediately (uncheck to require change on
											first login)
										</label>
									</div>
								</>
							)}

							<DialogFooter>
								{resetPasswordSuccess ? (
									<Button
										type="button"
										variant="outline"
										onClick={closeResetPasswordModal}
									>
										Done
									</Button>
								) : (
									<>
										<Button
											type="button"
											variant="outline"
											onClick={closeResetPasswordModal}
										>
											Cancel
										</Button>
										<Button type="submit" disabled={resetPasswordLoading}>
											{resetPasswordLoading ? "Setting…" : "Set password"}
										</Button>
									</>
								)}
							</DialogFooter>
						</form>
					</DialogContent>
				</Dialog>

				<SyncConfirmModal
					isOpen={showSyncConfirm}
					onClose={() => setShowSyncConfirm(false)}
					onConfirm={handleSyncConfirmed}
				/>

				{syncResult && (
					<SyncResultsModal
						isOpen={showSyncResults}
						onClose={() => {
							setShowSyncResults(false);
							setSyncResult(null);
						}}
						result={syncResult}
					/>
				)}
			</div>
		</div>
	);
};

export default UserManagement;
